import React, {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useMe, userApi } from '@modules/user/data'
import { authApi } from '@modules/auth/data'
import {
  clearAuthToken,
  clearSessionUser,
  getAuthToken,
  getProspectToken,
  getSessionUser,
} from '@utils/auth'
import { ISessionContext } from 'types/contexts/session'
import { User } from 'types/model'
import { SignupFormValues } from 'types/model/auth'
import googleTagManagerClient from '@utils/analytics/gtm'
import {
  identifyBuyLegacyConsumer,
  resetBuyLegacyPosthog,
} from '@lib/posthog'

export const sessionContext = createContext<ISessionContext>(
  {} as ISessionContext
)

export function SessionContext({ children }: { children?: ReactNode }) {
  const prospectToken = getProspectToken()
  const hadUserForPosthogRef = useRef(false)

  const [authError, setAuthError] = useState<string>()
  const [authToken, setAuthToken] = useState<string | undefined | null>(
    getAuthToken()
  )
  const [userId, setUserId] = useState<string | undefined>(getSessionUser())

  const { data: user, mutate } = useMe({ pause: !authToken || !userId })

  const updateProfile = useCallback(
    async (vals: Partial<User>) => {
      const response = await userApi.update('me', vals)
      await mutate(response)
      return response
    },
    [mutate]
  )

  const authRequest = useCallback(
    async (callback: () => Promise<{ user: string; token: string }>) => {
      try {
        const { user, token } = await callback()
        setUserId(user)
        setAuthToken(token)
        setAuthError(undefined)
        return true
      } catch (e) {
        if (e instanceof Error) {
          setAuthError(e.message)
          clearSessionUser()
          clearAuthToken()
          setUserId(undefined)
          setAuthToken(undefined)
        }
        return false
      }
    },
    []
  )

  const signup = useCallback(
    async ({
      firstName,
      lastName,
      email,
      password,
    }: Omit<SignupFormValues, 'confirmPassword'>) => {
      return await authRequest(async () => {
        const response = await authApi.signup({
          firstName,
          lastName,
          email,
          password,
        })
        return { user: response.user.id, token: response.token }
      })
    },
    [authRequest]
  )

  const login = useCallback(
    async (email: string, password: string) => {
      return await authRequest(async () => {
        const response = await authApi.login(email, password)
        return { user: response.user.id, token: response.token }
      })
    },
    [authRequest]
  )

  const logout = useCallback(async () => {
    await authApi.logout()
    setAuthError(undefined)
    setAuthToken(undefined)
    setUserId(undefined)
  }, [])

  const clearAuthError = useCallback(() => setAuthError(undefined), [])

  const contextValue = useMemo<ISessionContext>(
    () => ({
      prospectToken,
      authToken,
      authError,
      clearAuthError,
      user,
      updateProfile,
      loggedIn: !!authToken,
      signup,
      login,
      logout,
    }),
    [
      authError,
      authToken,
      clearAuthError,
      login,
      logout,
      prospectToken,
      signup,
      updateProfile,
      user,
    ]
  )

  React.useEffect(() => {
    if (user) {
      googleTagManagerClient.identifyUser({
        userId: user.id,
        traits: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone,
        },
      })
      identifyBuyLegacyConsumer(user)
      hadUserForPosthogRef.current = true
      return
    }
    if (hadUserForPosthogRef.current) {
      resetBuyLegacyPosthog()
      hadUserForPosthogRef.current = false
    }
  }, [user])

  return (
    <sessionContext.Provider value={contextValue}>
      {children}
    </sessionContext.Provider>
  )
}
